type CollectionLike = {
  id: number;
  name: string;
  location?: string | null;
  personal_owner_id?: number | null;
  archived?: boolean;
  order?: number;
  mbName?: string;
};

const sameId = (id: number, expected: number): boolean => Number(id) === expected;

export type CollectionPermissionGraph = {
  revision?: number;
  groups: Record<string, Record<string, string | undefined>>;
};

/** True when Metabase included the environment collection in the user's visible list. */
export const isEnvironmentCollectionListed = (
  collections: { id: number; archived?: boolean; personal_owner_id?: number | null }[] | null | undefined,
  environmentCollectionId: number
): boolean =>
  Array.isArray(collections) &&
  collections.some(
    collection =>
      sameId(collection.id, environmentCollectionId) && !collection.archived && collection.personal_owner_id == null
  );

/**
 * Give groups read access to one collection. Existing read or write is left as it is.
 * Returns a copy of the graph.
 */
export const withCollectionRead = (
  graph: CollectionPermissionGraph,
  collectionId: number,
  groupIds: number[]
): { graph: CollectionPermissionGraph; changed: boolean } => {
  const updated: CollectionPermissionGraph = JSON.parse(JSON.stringify(graph || { groups: {} }));
  if (!updated.groups) updated.groups = {};
  const collectionKey = String(collectionId);
  let changed = false;

  groupIds.forEach(groupId => {
    if (groupId == null) return;
    const groupKey = String(groupId);
    if (!updated.groups[groupKey]) updated.groups[groupKey] = {};
    const current = updated.groups[groupKey][collectionKey];
    if (current === 'read' || current === 'write') return;
    updated.groups[groupKey][collectionKey] = 'read';
    changed = true;
  });

  return { graph: updated, changed };
};

const isPersonalOrArchived = (collection: CollectionLike): boolean =>
  !!collection.archived || collection.personal_owner_id != null;

/** `name` is the label shown in ImpactBoard. `mbName` is the Metabase collection name. */
export type ParsedMetabaseCollection<T> = T & {
  order: number;
  mbName: string;
};

/**
 * System is the environment collection (Development, Staging, or Production).
 * My Organisation is this organisation's folder directly under that collection.
 * Write access must not hide either one: Premium admins can curate them.
 */
export const parseMetabaseCollections = <T extends CollectionLike>(
  collections: T[],
  environmentCollectionId: number,
  organisationCollectionName: string
): Array<ParsedMetabaseCollection<T>> => {
  const visible = (collections || []).filter(collection => !isPersonalOrArchived(collection));
  const system = visible.find(collection => sameId(collection.id, environmentCollectionId));
  const organisationLocation = `/${environmentCollectionId}/`;
  const underSystem = visible.filter(collection => collection.location === organisationLocation);
  const namedOrganisation = organisationCollectionName
    ? underSystem.find(collection => collection.name === organisationCollectionName)
    : undefined;
  const organisation = namedOrganisation || (underSystem.length === 1 ? underSystem[0] : undefined);

  const parsed: Array<ParsedMetabaseCollection<T>> = [];
  if (system) {
    parsed.push({
      ...system,
      name: 'System',
      order: 0,
      mbName: system.name,
    });
  }
  if (organisation) {
    parsed.push({
      ...organisation,
      name: 'My Organisation',
      order: 1,
      mbName: organisation.name,
    });
    const childPrefix = `/${environmentCollectionId}/${organisation.id}/`;
    visible.forEach(collection => {
      if (collection.location && collection.location.startsWith(childPrefix)) {
        parsed.push({
          ...collection,
          order: 2,
          mbName: collection.name,
        });
      }
    });
  }
  return parsed;
};

export const labelMetabaseCollection = <T extends CollectionLike>(
  collection: T,
  environmentCollectionId: number,
  organisationCollectionName: string
): ParsedMetabaseCollection<T> => {
  if (isPersonalOrArchived(collection)) {
    return { ...collection, order: 2, mbName: collection.name };
  }
  if (sameId(collection.id, environmentCollectionId)) {
    return { ...collection, name: 'System', order: 0, mbName: collection.name };
  }
  const isOrganisationFolder = collection.location === `/${environmentCollectionId}/`;
  const isThisOrganisation = !organisationCollectionName || collection.name === organisationCollectionName;
  if (isOrganisationFolder && isThisOrganisation) {
    return { ...collection, name: 'My Organisation', order: 1, mbName: collection.name };
  }
  return { ...collection, order: 2, mbName: collection.name };
};
